/**
 * The "what can legally go here" layer over an extractor snapshot.
 *
 * Both the validator and (later) the app's item editor read their rules from here, so the
 * two can never drift into disagreeing about what a legal item is. Everything in this file
 * is derived from the snapshot at runtime — nothing is hardcoded that the data can answer.
 *
 * Where a rule comes from Java rather than JSON, the source is quoted at the call site and
 * attributed to mahjerion/Mine-And-Slash-Rework @ `1.20-Forge`, which is the fork Craft to
 * Exile 2 actually ships. Upstream (RobertSkalko) is ~340 commits behind and will give wrong
 * answers.
 */
import { humanise } from "./display.js";
/**
 * Craft to Exile 2 runs "original" mode: it ships pack overrides for exactly
 * `original_balance` and `original_mode_player` and leaves the `compat_mode_*` entries at
 * their jar defaults. Both are overridable in case a future pack flips this.
 */
export const DEFAULT_BALANCE_ID = "original_balance";
export const DEFAULT_PLAYER_BASE_STATS_ID = "original_mode_player";
/**
 * `BaseStatsAdder.MOB` — the block every non-player entity gets.
 *
 * `CommonStatUtils.addBaseStats` is a three-way branch: a mercenary takes `mercenary`, a player
 * takes `CompatConfig.baseStatsDatapack()`, and **everything else takes this one**. It is not a
 * default or a fallback; a mob has it as surely as it has its rarity, and Craft to Exile 2
 * overrides it with a block that carries armour, dodge, spell dodge and 10 of every resist.
 */
export const MOB_BASE_STATS_ID = "mob";
/**
 * `UniqueStatsData.MAX_STATS` — how many roll slots a unique's NBT always carries, regardless
 * of how many stats it actually has. `RerollNumbers` fills all ten; `GetAllStats` reads only
 * as many as the unique has stats. A five-stat unique therefore stores ten rolls, five of
 * which do nothing.
 */
export const UNIQUE_ROLL_SLOTS = 10;
/**
 * One option out of `defaultconfigs/mine_and_slash-server.toml`.
 *
 * The extractor keeps the whole file as raw scalars, so reading an option is a lookup rather
 * than an extractor change. Keys are qualified by their TOML section, which for this file is
 * `general` for everything that matters — `serverConfigNumber(snapshot, "general.in_combat_regen_multi")`.
 *
 * Returns `undefined` rather than a default when the file was not found or the key is not a
 * number, so the caller can say what its own fallback is and why.
 */
export function serverConfigNumber(snapshot, key) {
    const value = snapshot.externalConfig?.serverConfig?.values[key];
    return typeof value === "number" ? value : undefined;
}
/** As `serverConfigNumber`, for a string-array option such as `general.gear_compatibility`. */
export function serverConfigList(snapshot, key) {
    const value = snapshot.externalConfig?.serverConfig?.values[key];
    return Array.isArray(value) ? value : undefined;
}
export const CATEGORY = {
    affix: "mmorpg_affixes",
    aura: "mmorpg_aura",
    baseGearType: "mmorpg_base_gear_types",
    baseStats: "mmorpg_base_stats",
    chaosStat: "mmorpg_chaos_stat",
    currency: "library_of_exile_currency",
    exileEffect: "mmorpg_exile_effect",
    gameBalance: "mmorpg_game_balance",
    gearRarity: "mmorpg_gear_rarity",
    gearSlot: "mmorpg_gear_slot",
    itemModification: "library_of_exile_item_modification",
    itemRequirement: "library_of_exile_item_requirement",
    itemSet: "mmorpg_sets",
    gem: "mmorpg_gems",
    mapAffix: "mmorpg_map_affix",
    mercenary: "mmorpg_mercenary",
    mobAffix: "mmorpg_mob_affix",
    mobRarity: "mmorpg_mob_rarity",
    omen: "mmorpg_omen",
    perk: "mmorpg_perk",
    rune: "mmorpg_runes",
    runeword: "mmorpg_runeword",
    spell: "mmorpg_spells",
    spellSchool: "mmorpg_spell_school",
    stat: "mmorpg_stat",
    statBuff: "mmorpg_stat_buff",
    statLayer: "mmorpg_stat_layer",
    statCompat: "mmorpg_stat_compat",
    statCondition: "mmorpg_stat_condition",
    statEffect: "mmorpg_stat_effect",
    supportGem: "mmorpg_support_gem",
    talentTree: "mmorpg_talent_tree",
    unique: "mmorpg_unique_gears",
    valueCalc: "mmorpg_value_calc",
    weaponType: "mmorpg_weapon_type",
    wizard: "mmorpg_wizard",
};
/** The ten `type` values seen across `mmorpg_affixes`. Anything else must fail loud. */
export const AFFIX_TYPES = [
    "prefix",
    "suffix",
    "implicit",
    "enchant",
    "chaos_stat",
    "jewel",
    "jewel_corruption",
    "crafted_jewel_unique",
    "watcher_eye",
    "tool",
];
export function isAffixType(value) {
    return AFFIX_TYPES.includes(value);
}
// ---------------------------------------------------------------------------
// Snapshot access
// ---------------------------------------------------------------------------
export function entry(snapshot, category, id) {
    return snapshot.registries[category]?.[id];
}
export function has(snapshot, category, id) {
    return entry(snapshot, category, id) !== undefined;
}
export function ids(snapshot, category) {
    return Object.keys(snapshot.registries[category] ?? {});
}
function data(snapshot, category, id) {
    return entry(snapshot, category, id)?.data;
}
// Narrow readers. Registry data is `unknown`-valued, and a missing or wrongly-typed field
// should surface as a diagnostic rather than a crash or a silent zero.
function num(node, key, fallback) {
    const v = node[key];
    return typeof v === "number" && Number.isFinite(v) ? v : fallback;
}
function str(node, key) {
    const v = node[key];
    return typeof v === "string" && v.length > 0 ? v : undefined;
}
function bool(node, key, fallback) {
    const v = node[key];
    return typeof v === "boolean" ? v : fallback;
}
function obj(node, key) {
    const v = node[key];
    return v !== null && typeof v === "object" && !Array.isArray(v) ? v : undefined;
}
function arr(node, key) {
    const v = node[key];
    return Array.isArray(v) ? v : [];
}
function minMax(node, fallback) {
    if (!node)
        return fallback;
    return { min: num(node, "min", fallback.min), max: num(node, "max", fallback.max) };
}
export function gearRarity(snapshot, id) {
    const d = data(snapshot, CATEGORY.gearRarity, id);
    if (!d)
        return undefined;
    return {
        id,
        itemTier: num(d, "item_tier", 0),
        affixRarityWeight: num(d, "affix_rarity_weight", 0),
        isUniqueItem: bool(d, "is_unique_item", false),
        type: str(d, "type") ?? "NORMAL",
        minAffixes: num(d, "min_affixes", 0),
        statPercents: minMax(obj(d, "stat_percents"), { min: 0, max: 100 }),
        baseStatPercents: minMax(obj(d, "base_stat_percents"), { min: 0, max: 100 }),
        sockets: minMax(obj(d, "sockets"), { min: 0, max: 0 }),
        maxGems: num(d, "max_gems", 0),
        maxRunes: num(d, "max_runes", 0),
        canHaveRunewords: bool(d, "can_have_runewords", false),
        minLvl: num(d, "min_lvl", 0),
    };
}
/**
 * The most sockets a corruption can add to an item of this rarity: the largest
 * `bonus_sockets` among the `mmorpg_chaos_stat` outcomes whose `for_item_rarities` names it.
 * In this pack that is the "Ascended" outcome, 1 on every rarity.
 */
export function corruptionSockets(snapshot, rarityId) {
    let most = 0;
    for (const id of ids(snapshot, CATEGORY.chaosStat)) {
        const d = data(snapshot, CATEGORY.chaosStat, id);
        if (d === undefined || !arr(d, "for_item_rarities").includes(rarityId))
            continue;
        most = Math.max(most, num(d, "bonus_sockets", 0));
    }
    return most;
}
/**
 * Item modification serializers that turn a common item straight into another rarity without
 * touching its sockets, keyed to the rarity they set.
 *
 * `CommonToEpicGuaranteedTagItemMod` and `CommonToMythicGuaranteedTagItemMod` hardcode
 * `rar = "epic"` / `"mythic"`, rescale base stats and reroll affixes, and never call
 * `removeSocket` (checked against the 6.4.13 jar). `UpgradeRarityItemMod` is the one that trims
 * to the new rarity's `sockets.max`. The currencies that run these require `is_common`.
 */
const SOCKET_KEEPING_UPGRADES = {
    common_to_epic_guaranteed_tag: "epic",
    common_to_mythic_guaranteed_tag: "mythic",
};
/**
 * The most sockets an uncorrupted item of this rarity can carry: its own `sockets.max`, or a
 * common's when the pack has a mod that turns a common into this rarity sockets and all — a
 * two-socket common through a Perfected Orb is a two-socket mythic.
 */
export function baseSocketCap(snapshot, rarityId) {
    let most = gearRarity(snapshot, rarityId)?.sockets.max ?? 0;
    const common = gearRarity(snapshot, "common")?.sockets.max ?? 0;
    if (common <= most)
        return most;
    for (const id of ids(snapshot, CATEGORY.itemModification)) {
        const serializer = str(data(snapshot, CATEGORY.itemModification, id) ?? {}, "serializer");
        if (serializer !== undefined && SOCKET_KEEPING_UPGRADES[serializer] === rarityId) {
            most = common;
            break;
        }
    }
    return most;
}
/**
 * How many sockets this item has room for: {@link baseSocketCap}, plus whatever its corruption
 * added (`Item.bonusSockets`). Gems and runes share them.
 */
export function socketCap(snapshot, item) {
    return baseSocketCap(snapshot, item.rarity) + (item.bonusSockets ?? 0);
}
/**
 * How many prefixes plus suffixes an item of this rarity has — exactly, not at least.
 *
 * `GearRarity.getAffixAmount()` returns `min_affixes`, and `GearAffixesData.randomize()`
 * tops the item up until it has that many:
 *
 *     int minaffixes = rar.min_affixes;
 *     int affixesToGen = minaffixes - (this.getNumberOfAffixes());
 *     while (affixesToGen > 0) { addOneRandomAffix(gear); affixesToGen--; }
 *
 * so the field name is misleading: it is a fixed count. common 1 -> mythic 6.
 */
export function affixCount(rarity) {
    return rarity.minAffixes;
}
/**
 * The most prefixes (or suffixes) a single item can carry.
 *
 * `IGearRarity.maximumOfOneAffixType()` is `getAffixAmount() / 2`, and `randomize()` seeds
 * that many of *each* type before topping up. But the top-up path does not re-check that
 * cap — `addOneRandomAffix()` only balances the two counts:
 *
 *     if (getNumberOfPrefixes() > getNumberOfSuffixes()) { ...suffix... } else { ...prefix... }
 *
 * so an odd `min_affixes` produces one more of the favoured type than
 * `maximumOfOneAffixType()` reports. Legendary (5) ends up 3 prefixes / 2 suffixes even
 * though the method returns 2. The observable ceiling is therefore the rounded-up half.
 */
export function maxOfOneAffixType(rarity) {
    return rarity.minAffixes - Math.floor(rarity.minAffixes / 2);
}
/**
 * The rarities an affix on this item may carry as its own tier.
 *
 *     var list = ExileDB.GearRarities()
 *         .getFilterWrapped(x -> !x.is_unique_item && rar.item_tier >= x.item_tier)
 *
 * — `AffixData.randomizeTier`. Note this is the *item's* rarity bounding the affix's, which
 * is why a mythic item can carry a common-tier affix but never the reverse.
 */
export function allowedAffixTiers(snapshot, itemRarity) {
    return ids(snapshot, CATEGORY.gearRarity)
        .map((id) => gearRarity(snapshot, id))
        .filter((r) => r !== undefined)
        .filter((r) => !r.isUniqueItem && itemRarity.itemTier >= r.itemTier)
        // In the ladder's order, not the registry's. `ids()` is alphabetical, so every tier dropdown
        // in the app read "common, epic, legendary, mythic, rare, uncommon" — a list whose order
        // says nothing, over a value whose whole meaning is its rank. `item_tier` is that rank, and
        // no unique survives the filter above, so there is no tie here to break.
        .sort((a, b) => a.itemTier - b.itemTier)
        .map((r) => r.id);
}
/**
 * The rarity ladder, as `higher_rar` spells it — common, uncommon, rare, epic, legendary, mythic.
 *
 * Not the same list as {@link allowedAffixTiers}, and the difference is the point. An affix's
 * tier is bounded by the *item's* rarity; a ladder is a chain the data states outright:
 *
 *     public boolean hasHigherRarity() { return ExileDB.GearRarities().isRegistered(higher_rar); }
 *     public GearRarity getHigherRarity() { return ExileDB.GearRarities().get(higher_rar); }
 *
 * — GearRarity.java:193-202. Walking it rather than sorting on `item_tier` is what leaves
 * `unique` and `runeword` out without naming them: nothing links to either, and `mythic` links
 * to nothing, so the chain is exactly the six a currency can climb.
 *
 * This is what an **infusion** is graded on. `GearInfusionData.rar` is its own rarity, separate
 * from the item's, and `CraftedInfusionItem.canBeModified` refuses any step but the next one up:
 *
 *     if (!data.ench.canUpgradeToRarity(rar)) return ExplainedResult.failure(...)
 *
 * so a mythic infusion is six successful infusings, on an item of any rarity at all.
 */
export function rarityLadder(snapshot, from = "common") {
    const chain = [];
    let id = from;
    while (id !== undefined && id.length > 0 && !chain.includes(id)) {
        const d = data(snapshot, CATEGORY.gearRarity, id);
        if (!d)
            break;
        chain.push(id);
        id = str(d, "higher_rar");
    }
    return chain;
}
/**
 * What an infusion of this rarity rolls at — always the top of its band, never anything else.
 *
 *     public int getPercent() {
 *         return ExileDB.GearRarities().get(rar).stat_percents.max;
 *     }
 *
 * — GearInfusionData.java:63-65, confirmed in the 6.4.13 jar. `GearInfusionData` stores `en` and
 * `rar` and **no percent at all**, so an infusion does not roll: a common one on
 * `ench_pants_damage_when_hit` (10-30) is 13.4 every time, because common's band tops out at 17.
 *
 * The document still records a `rollPercent` alongside the tier, because an `AffixRoll` is one
 * shape everywhere and an infusion is stored as one. This is the only value that shape may
 * legally carry, which is why the editor writes it rather than offering a slider and the
 * validator reports anything else.
 */
export function infusionRollPercent(snapshot, tier) {
    return gearRarity(snapshot, tier)?.statPercents.max;
}
function numberMap(node) {
    const out = {};
    for (const [key, value] of Object.entries(node ?? {})) {
        if (typeof value === "number" && Number.isFinite(value))
            out[key] = value;
    }
    return out;
}
export function baseGearType(snapshot, id) {
    const d = data(snapshot, CATEGORY.baseGearType, id);
    if (!d)
        return undefined;
    const tagNode = obj(d, "tags");
    return {
        id,
        gearSlot: str(d, "gear_slot"),
        tags: tagNode ? arr(tagNode, "tags").filter((t) => typeof t === "string") : [],
        baseStats: arr(d, "base_stats").filter((s) => s !== null && typeof s === "object" && !Array.isArray(s)),
        weaponType: str(d, "weapon_type"),
        itemIds: arr(d, "possible_items")
            .filter((i) => i !== null && typeof i === "object")
            .map((i) => i["item_id"])
            .filter((id) => typeof id === "string"),
        req: {
            baseReq: numberMap(obj(obj(d, "req") ?? {}, "base_req")),
            scalingReq: numberMap(obj(obj(d, "req") ?? {}, "scaling_req")),
        },
    };
}
export function affix(snapshot, id) {
    const d = data(snapshot, CATEGORY.affix, id);
    if (!d)
        return undefined;
    const reqs = obj(d, "requirements");
    return {
        id,
        type: str(d, "type") ?? "",
        tagRequirements: (reqs ? arr(reqs, "tag_requirements") : [])
            .filter((r) => r !== null && typeof r === "object" && !Array.isArray(r))
            .map((r) => ({
            reqType: str(r, "req_type") ?? "",
            included: arr(r, "included").filter((t) => typeof t === "string"),
            excluded: arr(r, "excluded").filter((t) => typeof t === "string"),
        })),
        stats: arr(d, "stats").filter((s) => s !== null && typeof s === "object" && !Array.isArray(s)),
        onlyOnePerItem: bool(d, "only_one_per_item", false),
        oneOfAKind: str(d, "one_of_a_kind") ?? "",
        eyeAuraReq: str(d, "eye_aura_req") ?? "",
    };
}
/**
 * Whether a set of tags satisfies one tag requirement.
 *
 * `TagRequirement.meetsRequierment()` checks exclusions first and returns false on any hit,
 * then applies the mode to the included list. Both `req_type` values are in use in this
 * pack: `INCLUDES_ANY` (438 affixes) and `HAS_ALL` (51).
 */
export function meetsTagRequirement(req, tags) {
    for (const excluded of req.excluded) {
        if (tags.includes(excluded))
            return false;
    }
    if (req.included.length === 0)
        return true;
    if (req.reqType === "HAS_ALL")
        return req.included.every((t) => tags.includes(t));
    if (req.reqType === "INCLUDES_ANY")
        return req.included.some((t) => tags.includes(t));
    // Unknown mode: refuse to guess. Callers surface this as a fail-loud diagnostic.
    return false;
}
export function isKnownReqType(reqType) {
    return reqType === "HAS_ALL" || reqType === "INCLUDES_ANY";
}
/** All tag requirements must pass for the affix to be available on those tags. */
export function affixAllowedOnTags(view, tags) {
    return view.tagRequirements.every((req) => meetsTagRequirement(req, tags));
}
/**
 * Every affix of `type` that can roll on `baseId` — the pool an item editor offers.
 *
 * Mirrors the filter in `AffixData.RerollFully`:
 *     x -> x.type == getAffixType() && gear.canGetAffix(x)
 */
export function affixesFor(snapshot, baseId, type) {
    const base = baseGearType(snapshot, baseId);
    if (!base)
        return [];
    return ids(snapshot, CATEGORY.affix)
        .map((id) => affix(snapshot, id))
        .filter((a) => a !== undefined)
        .filter((a) => a.type === type && affixAllowedOnTags(a, base.tags));
}
/**
 * `PlayStyle` ids — what `JewelItemData.style` holds, and the three jewels there are.
 *
 * `PlayStyle.fromID` falls back to `STR` on anything it does not know, and so does everything
 * here that takes one.
 */
export const JEWEL_STYLES = ["str", "dex", "int"];
export function isJewelStyle(value) {
    return JEWEL_STYLES.includes(value);
}
/**
 * The tag a jewel of this style carries, so an affix can ask for it — `SlotTags.jewel_str`
 * and friends, via `PlayStyle.getJewelAffixTag()`.
 */
export function jewelTags(style) {
    return ["any_jewel", `jewel_${isJewelStyle(style ?? "") ? style : "str"}`];
}
/**
 * Every `jewel` affix a jewel of this style may roll — the pool a jewel editor offers.
 *
 * Mirrors the filter in `JewelItemData.generateAffixes`:
 *
 *     x -> x.type == Affix.AffixSlot.jewel
 *          && (x.getAllTagReq().contains(SlotTags.any_jewel.GUID())
 *              || x.getAllTagReq().contains(getStyle().getJewelAffixTag().GUID()))
 *
 * The Java asks whether the affix *mentions* either tag rather than evaluating the
 * requirement, which is the same question only because all 53 jewel affixes declare a single
 * `INCLUDES_ANY` with nothing excluded. {@link affixAllowedOnTags} therefore agrees with it
 * exactly, and it is the one that stays right if the pack ever ships a `HAS_ALL` jewel affix.
 *
 * Of the 53, 36 are `any_jewel`: a Viridian jewel may carry 44 and a Meteorite or Stardust 45.
 */
export function jewelAffixesFor(snapshot, style) {
    const tags = jewelTags(style);
    return ids(snapshot, CATEGORY.affix)
        .map((id) => affix(snapshot, id))
        .filter((a) => a !== undefined)
        .filter((a) => a.type === "jewel" && affixAllowedOnTags(a, tags));
}
/** Every affix of one type, in id order — the pools that are filtered by nothing but the type. */
function affixesOfType(snapshot, type) {
    return ids(snapshot, CATEGORY.affix)
        .map((id) => affix(snapshot, id))
        .filter((a) => a !== undefined && a.type === type)
        .sort((a, b) => a.id.localeCompare(b.id));
}
/**
 * The Augment lines an Abyssal Eye may carry — every `watcher_eye` affix, unfiltered.
 *
 * `WatcherEyeBlueprint` sets `JewelBlueprint.isEye`, and that branch of `createData` draws from
 * the type alone:
 *
 *     while (data.auraStats.size() < auraAffixes) {
 *         Affix a = ExileDB.Affixes().getFilterWrapped(x -> x.type == AffixSlot.watcher_eye).random();
 *         if (data.auraStats.stream().noneMatch(x -> x.affix.equals(a.GUID())))
 *             data.auraStats.add(new StatsWhileUnderAuraData(a, level));
 *     }
 *
 * — `Mine_and_Slash-1.20.1-6.4.13.jar`. No tag requirement is consulted and the jewel's style
 * does not narrow it, which is why this takes no style: the eye is always a `str`-tagged pool's
 * problem for its *rolled* affixes and no pool's problem for these. The `noneMatch` is the one
 * rule there is — the same affix never appears twice on one eye, which `only_one_per_item`
 * already says on all 33 of them.
 */
export function watcherEyeAffixes(snapshot) {
    return affixesOfType(snapshot, "watcher_eye");
}
/**
 * What an Orb of Mesmerizing Chaos can put on a jewel — every `jewel_corruption` affix.
 *
 *     if (cor.isEmpty()) {
 *         int num = RandomUtils.roll(10) ? 2 : 1;
 *         for (int i = 0; i < num; i++) {
 *             Affix a = rollAffix(cor, x -> x.type == AffixSlot.jewel_corruption);
 *             ...
 *         }
 *     }
 *
 * — `JewelItemData.corrupt`, same jar. Type only, again: the style tags never come into it, so
 * a Meteorite and a Stardust jewel corrupt out of the same pool. Two is the ceiling, and a
 * corrupted jewel can never be corrupted again.
 */
export function jewelCorruptionAffixes(snapshot) {
    return affixesOfType(snapshot, "jewel_corruption");
}
/**
 * `JewelItemData.corrupt` rolls one, or two on a 1-in-10 — so two is the most a jewel carries.
 */
export const MAX_JEWEL_CORRUPTIONS = 2;
/**
 * How many Augment lines an Abyssal Eye can carry.
 *
 * `WatcherEyeBlueprint` reads it off the uber boss that dropped the eye —
 * `UberBossTier.watcherEyeAffixes`, which is 1, 2 or 3 as the tier goes up (tiers registered at
 * levels 40, 60 and 90 in the same jar). Three is the ceiling, and there is no way to add a
 * fourth afterwards.
 */
export const MAX_EYE_AURA_STATS = 3;
/** `CraftedUniqueJewelData.WATCHER_EYE` — the `unique.id` that marks a jewel as an Abyssal Eye. */
export const WATCHER_EYE_UNIQUE = "watcher_eye";
export function unique(snapshot, id) {
    const d = data(snapshot, CATEGORY.unique, id);
    if (!d)
        return undefined;
    return {
        id,
        baseGear: str(d, "base_gear"),
        minDropLvl: num(d, "min_drop_lvl", 0),
        rarity: str(d, "rarity"),
        replacesName: bool(d, "replaces_name", true),
        uniqueStats: arr(d, "unique_stats").filter((s) => s !== null && typeof s === "object" && !Array.isArray(s)),
    };
}
export function uniquesForBase(snapshot, baseId) {
    return ids(snapshot, CATEGORY.unique)
        .map((id) => unique(snapshot, id))
        .filter((u) => u !== undefined && u.baseGear === baseId);
}
export function basesForSlot(snapshot, slotId) {
    return ids(snapshot, CATEGORY.baseGearType)
        .map((id) => baseGearType(snapshot, id))
        .filter((b) => b !== undefined && b.gearSlot === slotId);
}
/** `gear_slot.fam`, e.g. "Armor" — the family a slot belongs to. */
export function slotFamily(snapshot, slotId) {
    const d = data(snapshot, CATEGORY.gearSlot, slotId);
    return d ? str(d, "fam") : undefined;
}
const CENTER_TOKEN = "[center]";
/**
 * Parses a tree's CSV grid, classifying cells exactly as `GridPoint.java:26-41` does.
 *
 *     this.id = str.toLowerCase(Locale.ROOT);
 *     if (id.length() == 1)                     { this.isConnector = true; }
 *     else if (id.equalsIgnoreCase(CENTER_ID))  { this.isCenter = true; }
 *     else if (id.length() > 2)                 { this.isTalent = true; }
 *
 * Three consequences the previous "is it a registered perk id" rule got wrong:
 *
 *   - **`E` is a connector, not empty.** It is a single character, so it classifies as a
 *     connector with glyph `"e"`. All 618 per tree form the border ring, which is what stops
 *     the game's unguarded `get(x ± 1, y ± 1)` from running off the array. They are isolated
 *     from every talent, so no edge is ever drawn along them — but the classification has to
 *     be right or the guard's purpose is invisible.
 *   - **A token of exactly two characters is nothing at all** — it falls through all three
 *     branches. There are none today; if a pack adds one it silently disappears in game too.
 *   - **A token longer than two characters is a talent whether or not it is registered.**
 *
 * Note the grids are not valid strict JSON in their source files — the `perks` value contains
 * raw newlines, which Gson tolerates. The extractor's lenient parser has already dealt with
 * that by the time a snapshot exists.
 */
/**
 * Cached per snapshot and tree id, including the `undefined` miss.
 *
 * The talents grid is 138 x 173 and the parse splits ~24,000 tokens. `collectPerks` calls this
 * once per tree per calculation, and an editor recalculates on every click. A `TreeGrid` closes
 * over an immutable array, so sharing is safe.
 */
const GRID_CACHE = new WeakMap();
export function treeGrid(snapshot, treeId) {
    let byId = GRID_CACHE.get(snapshot);
    if (!byId) {
        byId = new Map();
        GRID_CACHE.set(snapshot, byId);
    }
    if (byId.has(treeId))
        return byId.get(treeId);
    const built = buildTreeGrid(snapshot, treeId);
    byId.set(treeId, built);
    return built;
}
function buildTreeGrid(snapshot, treeId) {
    const d = data(snapshot, CATEGORY.talentTree, treeId);
    if (!d)
        return undefined;
    const raw = d["perks"];
    if (typeof raw !== "string")
        return undefined;
    const perks = snapshot.registries[CATEGORY.perk] ?? {};
    let center;
    const unknown = new Set();
    const grid = raw.split("\n").map((line, row) => line.split(",").map((token, col) => {
        const trimmed = token.trim();
        const id = trimmed.toLowerCase();
        if (id.length === 1)
            return { kind: "connector", raw: trimmed, id, glyph: id };
        if (id === CENTER_TOKEN) {
            center ??= [row, col];
            return { kind: "center", raw: trimmed, id };
        }
        if (id.length > 2) {
            if (!(id in perks))
                unknown.add(id);
            return { kind: "perk", raw: trimmed, id, perkId: id };
        }
        return { kind: "empty", raw: trimmed, id };
    }));
    let perkCellCount = 0;
    for (const row of grid) {
        for (const cell of row)
            if (cell.kind === "perk")
                perkCellCount++;
    }
    return {
        id: treeId,
        rows: grid.length,
        cols: grid.reduce((max, row) => Math.max(max, row.length), 0),
        cellAt: (row, col) => grid[row]?.[col],
        perkCellCount,
        center,
        unknownPerkIds: [...unknown].sort(),
    };
}
export function perk(snapshot, id) {
    const d = data(snapshot, CATEGORY.perk, id);
    if (!d)
        return undefined;
    return {
        id,
        type: str(d, "type") ?? "STAT",
        isEntry: d["is_entry"] === true,
        // `str` already collapses "" to undefined, which matches the game's
        // `one_kind != null && !one_kind.isEmpty()` guard.
        oneKind: str(d, "one_kind"),
        maxLevels: num(d, "max_lvls", 1),
        icon: str(d, "icon") ?? "",
    };
}
/** Every perk carrying `one_kind === kind`, e.g. the sixteen ascendancy entry perks. */
export function perksOfKind(snapshot, kind) {
    return ids(snapshot, CATEGORY.perk)
        .filter((id) => perk(snapshot, id)?.oneKind === kind)
        .sort();
}
/**
 * `player_points` key for each tree, matching the tree's own `school_type`.
 *
 * Exported because the app's point counters need the same mapping to ask
 * {@link pointsAvailable} about a tree, and two copies of it is how the tree screen and the
 * validator would drift apart again.
 */
export const TREE_POINT_TYPE = {
    talents: "TALENTS",
    ascendancy: "ASCENDANCY",
    atlas: "ATLAS",
};
/**
 * Point budget for a tree at a given level, from `mmorpg_game_balance.player_points`.
 *
 * `maxBonus` is included in `ceiling` because bonus points come from sources a build
 * document does not record. Spending more than `fromLevel` is therefore suspicious rather
 * than impossible, and the validator reports it as a warning.
 *
 * A tree's pool is just one of the six `PlayerPointsType` keys, so this is
 * {@link playerPointBudget} with the tree's name looked up. It stays as its own function
 * because `TreeKey` is what every caller on the tree side already holds.
 */
export function pointBudget(snapshot, tree, level, balanceId = DEFAULT_BALANCE_ID) {
    return playerPointBudget(snapshot, TREE_POINT_TYPE[tree], level, balanceId);
}
export function maxLevel(snapshot, balanceId = DEFAULT_BALANCE_ID) {
    const d = data(snapshot, CATEGORY.gameBalance, balanceId);
    return d ? num(d, "MAX_LEVEL", 100) : 100;
}
/**
 * `GameBalanceConfig.MAX_BONUS_SPELL_LEVELS` — how many ranks above a spell's own `max_lvl`
 * gear and perks may push it.
 *
 *     for (InsertedSpell spell : this.spells) {
 *         spell.bonus_ranks = MathHelper.clamp(spell.bonus_ranks, 0, GameBalanceConfig.get().MAX_BONUS_SPELL_LEVELS);
 *         spell.rank += spell.bonus_ranks;
 *     }
 *
 * — SpellCastingData.calcSpellLevels:135-139. The jar default is 5; `original_balance`, which
 * this pack runs, sets **8**. `@cte2/engine`'s `Balance` reads the same field for
 * `LeveledValue` interpolation — this copy exists so the validator does not need an engine.
 */
export function maxBonusSpellLevels(snapshot, balanceId = DEFAULT_BALANCE_ID) {
    const d = data(snapshot, CATEGORY.gameBalance, balanceId);
    return d ? num(d, "MAX_BONUS_SPELL_LEVELS", 5) : 5;
}
// ---------------------------------------------------------------------------
// Two-handed weapons
// ---------------------------------------------------------------------------
/**
 * The tag Craft to Exile 2 marks two-handed bases with.
 *
 * It appears **nowhere in the Mine and Slash source** — grep the 1.20-Forge checkout and there
 * is no `two_handed`, no `TwoHand`, nothing. It is a pure pack tag, carried by exactly three
 * bases (`greatsword`, `scythe`, `spear`) and read by 29 affixes as a roll requirement.
 *
 * The behaviour the player sees comes from **Better Combat**, not from the mod this project
 * otherwise ports. `net.bettercombat.mixin.PlayerEntityMixin.getEquippedStack_Pre` intercepts
 * every `getItemBySlot` call and, when the slot asked for is `OFFHAND` and either the selected
 * mainhand stack or the offhand stack carries two-handed weapon attributes, sets the return
 * value to `ItemStack.EMPTY` and cancels. Mine and Slash's `GearData` then reads an empty
 * offhand like any other, so an offhand item contributes *nothing at all* while a two-handed
 * weapon is held — not a partial penalty, and not merely a UI-level block.
 *
 * The two systems only agree because the pack makes them agree, which is worth recording since
 * a pack update could break it: the jar ships `bettercombat:staff` and `bettercombat:hammer` as
 * `two_handed: true`, and `cte_configuration` overrides both to `false`; RoE's spear items point
 * at `bettercombat:trident` (`two_handed: false`) and the same datapack repoints `spear_0..7` to
 * `bettercombat:spear` (`true`). After those overrides the Better Combat set and the tag set are
 * the same three bases.
 *
 * The consequence for this file is the happy one: two-handedness is answerable from the
 * snapshot the extractor already produces, with no Better Combat parsing anywhere.
 */
export const TWO_HANDED_TAG = "two_handed";
/** Whether this base occupies both hands, and so suppresses the offhand entirely. */
export function isTwoHanded(snapshot, baseId) {
    return baseGearType(snapshot, baseId)?.tags.includes(TWO_HANDED_TAG) ?? false;
}
// ---------------------------------------------------------------------------
// Dual wielding: a one-handed weapon in the offhand
// ---------------------------------------------------------------------------
/** `SlotTags.weapon_family`, which `DualWieldUtils` checks before it looks at the weapon type. */
const WEAPON_FAMILY_TAG = "weapon_family";
/** `can_dual_wield` on this base's `mmorpg_weapon_type`, or `undefined` for a non-weapon. */
function canDualWield(snapshot, baseId) {
    const base = baseGearType(snapshot, baseId);
    if (base === undefined || !base.tags.includes(WEAPON_FAMILY_TAG))
        return undefined;
    if (base.weaponType === undefined)
        return undefined;
    return data(snapshot, CATEGORY.weaponType, base.weaponType)?.["can_dual_wield"] === true;
}
/**
 * Whether this base may be held in the offhand — `DualWieldUtils.isDualWieldWeapon`.
 *
 * The pack's `mmorpg_weapon_type` registry is the whole answer: axe, dagger, gauntlet, hammer,
 * staff and sword carry `can_dual_wield: true`; bow, crossbow, greatsword, scythe, spear and
 * trident do not. Checked against the 6.4.13 jar.
 */
export function dualWieldable(snapshot, baseId) {
    return canDualWield(snapshot, baseId) === true;
}
/**
 * Whether a mainhand weapon of this base stops an offhand *weapon* from counting —
 * `DualWieldUtils.mainHandBlocksOffhandWeapon`, which is `isTwoHandedWeapon(mainhand)`.
 *
 * Mine and Slash's own two-handed test, and not the same set as {@link isTwoHanded}: it is every
 * weapon that cannot be dual wielded, so a bow or a trident blocks an offhand sword too. A shield
 * or tome is unaffected by it — `isUsableBy` only applies it to a weapon in `OFFHAND`.
 */
export function blocksOffhandWeapon(snapshot, baseId) {
    return canDualWield(snapshot, baseId) === false;
}
/** Whether this item may be worn in both places of a pair — both ring slots, or both hands. */
export function canMirror(snapshot, item) {
    const slotId = baseGearType(snapshot, item.base)?.gearSlot;
    if (slotId === undefined)
        return false;
    if ((SLOT_CAPACITY[slotId] ?? 1) >= 2)
        return true;
    return dualWieldable(snapshot, item.base);
}
/**
 * `doc.gear` as the pieces actually worn: a `mirrored` item becomes two, the second in the other
 * ring slot or in the offhand.
 *
 * Everything that counts, sums or searches worn gear reads this rather than `doc.gear`, so a
 * mirrored ring is two rings to the stat sheet, the set counter and the omen alike. Idempotent —
 * the pieces it returns carry no `mirrored` flag — so a helper handed either list answers the
 * same. A flag on an item that cannot be mirrored is ignored here and reported by the validator.
 */
export function wornPieces(snapshot, gear) {
    return gear.flatMap((item, index) => {
        if (item.mirrored !== true || !canMirror(snapshot, item))
            return [{ item, index, mirror: false }];
        const { mirrored: _mirrored, offhand: _offhand, ...plain } = item;
        const second = dualWieldable(snapshot, item.base) ? { ...plain, offhand: true } : plain;
        return [
            { item: plain, index, mirror: false },
            { item: second, index, mirror: true },
        ];
    });
}
/** {@link wornPieces} without the bookkeeping. */
export function wornItems(snapshot, gear) {
    return wornPieces(snapshot, gear).map((piece) => piece.item);
}
/** Whether this worn item is a weapon in the mainhand — the one a swing and `WEAPON_TYPE` use. */
export function isMainhandWeapon(snapshot, item) {
    if (item.offhand === true)
        return false;
    const slotId = baseGearType(snapshot, item.base)?.gearSlot;
    return slotId !== undefined && slotFamily(snapshot, slotId) === "Weapon";
}
/** The mainhand weapon, if one is worn. */
export function mainhandWeapon(snapshot, gear) {
    return wornItems(snapshot, gear).find((item) => isMainhandWeapon(snapshot, item));
}
/**
 * Whether a worn offhand weapon grants anything at all — `GearData.isUsableBy` for `OFFHAND`.
 *
 *     if (type.isWeapon()) {
 *         if (type.weaponType().can_dual_wield) {
 *             if (slot == EquipmentSlot.OFFHAND) {
 *                 return !DualWieldUtils.mainHandBlocksOffhandWeapon(data.getEntity());
 *             }
 *         }
 *         return slot == EquipmentSlot.MAINHAND;
 *     }
 *
 * So a weapon that cannot be dual wielded grants nothing from the offhand, and one that can
 * grants nothing beside a mainhand that blocks it. An empty mainhand does not block.
 */
export function offhandWeaponCounts(snapshot, item, gear) {
    if (item.offhand !== true || !dualWieldable(snapshot, item.base))
        return false;
    const main = mainhandWeapon(snapshot, gear);
    return main === undefined || !blocksOffhandWeapon(snapshot, main.base);
}
/**
 * `DualWieldUtils.isDualWielding` — a dual-wieldable weapon in *each* hand. What the
 * `is_dual_wielding` stat condition asks, and so what `dual_wield_damage` is gated on.
 */
export function isDualWielding(snapshot, gear) {
    const main = mainhandWeapon(snapshot, gear);
    if (main === undefined || !dualWieldable(snapshot, main.base))
        return false;
    return wornItems(snapshot, gear).some((item) => item.offhand === true && dualWieldable(snapshot, item.base));
}
/** `PERC_OFFHAND_WEP_STAT`'s default in `ServerContainer` — also what the pack ships. */
export const DEFAULT_OFFHAND_WEAPON_STAT_PERCENT = 25;
/**
 * The fraction of an offhand weapon's stats the character gets —
 * `DualWieldUtils.getOffhandStatPercent`, divided by 100.
 *
 *     PERC_OFFHAND_WEP_STAT.get() * Math.max(0, 1F + effectiveness / 100F)
 *
 * Effectiveness multiplies the share rather than adding to it, so +10% turns 25% into 27.5%.
 * `GearData` then multiplies *every* stat the item carries by it — base stats, affixes, sockets
 * and the unique's own lines alike.
 */
export function offhandWeaponShare(snapshot, effectiveness) {
    const percent = serverConfigNumber(snapshot, "general.PERC_OFFHAND_WEP_STAT") ??
        DEFAULT_OFFHAND_WEAPON_STAT_PERCENT;
    return (percent / 100) * Math.max(0, 1 + effectiveness / 100);
}
/**
 * Every enchantment this pack converts, in registry order.
 *
 * An entry with no `enchant_id` is the *attribute* half of `mmorpg_stat_compat` — vanilla
 * attributes rather than enchantments — and belongs to a different context (`VANILLA_STAT_COMPAT`
 * against `ENCHANT_COMPAT`), so it is filtered out rather than shown as an enchantment nobody
 * can apply.
 */
export function enchantCompats(snapshot) {
    const out = [];
    for (const id of ids(snapshot, CATEGORY.statCompat)) {
        const d = data(snapshot, CATEGORY.statCompat, id);
        if (d === undefined)
            continue;
        const enchantId = str(d, "enchant_id");
        if (enchantId === undefined || enchantId.length === 0)
            continue;
        out.push({
            id,
            enchantId,
            statId: str(d, "mns_stat_id") ?? "",
            // The Java field defaults, for an entry that omits them — the same ones `stat-compat.ts`
            // reads, because a bound shown in the editor that the engine does not use is a lie.
            conversion: num(d, "conversion", 0.5),
            modType: str(d, "mod_type") ?? "PERCENT",
            perItem: { min: num(d, "per_item_min", 0), max: num(d, "per_item_max", 100) },
            cap: { min: num(d, "minimum_cap", 0), max: num(d, "maximum_cap", 100) },
        });
    }
    return out;
}
/**
 * A readable name for an enchantment id.
 *
 * There is no name for these anywhere in the snapshot: `mmorpg_stat_compat` carries the registry
 * id and nothing else, and the enchantment itself belongs to Minecraft or to another mod, not to
 * Mine and Slash. So the id is humanised — `minecraft:fire_protection` becomes "Fire Protection"
 * — and the namespace is kept for anything that is not vanilla, because "Reinforced" appearing
 * twice with no hint that one is WRD's is worse than a slightly longer label.
 */
export function enchantName(enchantId) {
    const [namespace, path] = enchantId.includes(":")
        ? enchantId.split(":", 2)
        : ["minecraft", enchantId];
    const name = humanise(path);
    return namespace === "minecraft" ? name : `${name} (${namespace})`;
}
/**
 * A readable name for a vanilla attribute id.
 *
 * The same nowhere-to-look-it-up problem {@link enchantName} has, with one extra step: the
 * vanilla ones are `minecraft:generic.max_health` and the `generic.` is noise in every place a
 * player reads it. The namespace stays on everything else and is the useful half there —
 * `kubejs:magic_shield` is a food-diversity benefit rather than anything Minecraft ships, and a
 * bare "Magic Shield" beside a real stat of that name would read as a duplicate.
 */
export function attributeName(attributeId) {
    const [namespace, path] = attributeId.includes(":")
        ? attributeId.split(":", 2)
        : ["minecraft", attributeId];
    const name = humanise(path.replace(/^generic\./, ""));
    return namespace === "minecraft" ? name : `${name} (${namespace})`;
}
// ---------------------------------------------------------------------------
// Item quality
// ---------------------------------------------------------------------------
/**
 * How high `CustomItemData.KEYS.QUALITY` can go on one item in this pack.
 *
 * Quality itself is unclamped. `GearItemData.getQualityBaseStatsBonus` returns the stored int
 * with no arithmetic on it at all, and `BaseStatsData.GetAllStats` adds it to the roll with a
 * bare `iadd` and no bound (both read off the **6.4.13 jar**, not the fork). So the only thing
 * that limits quality is which currencies exist and what they refuse to be used on — which is
 * pack data, not code, and that distinction is the whole reason this is derived rather than
 * written down.
 *
 * Reading the Java would have given the wrong number. `ItemMods.ADD_UP_TO_5_GEAR_QUALITY` is
 * declared `new MinMax(1, 5)` and is even *named* for it, but Craft to Exile 2 overrides the
 * entry: `add_1_to_5_gear_quality` in `library_of_exile_item_modification` says
 * `{"min": 1, "max": 10}`. The pack wins, so the pack is what this counts.
 *
 * ## The arithmetic
 *
 * Two shapes of limit appear on a quality currency's `req` list:
 *
 *  - **a quality gate** — `is_under_quality`, which passes while `quality < max_quality`
 *    (`IsUnderQualityReq.isGearValid`). A gate `g` with a largest add `a` therefore tops out at
 *    `g - 1 + a`: sit one below the gate, then take the best roll.
 *  - **a use budget** — `max_uses`, counted per `use_id` on the item itself
 *    (`MaximumUsesReq.isValid`) and *shared* between every currency naming that id, which is
 *    what stops the six sharpening stones from being six separate uses. Those have no quality
 *    gate, so they add on top of whatever the gated ones reached.
 *
 * A currency's own best single application is every `always_do_item_mods` entry (they all fire)
 * plus the best `pick_one_item_mod` entry (one fires).
 *
 * As of 2.0.2 that resolves to **42**: Orb of Quality reaches 20, the Entangled Orb of Quality
 * is gated at 21 and adds up to 10 for 30, and a Godly Sharpening Stone adds 12 once more.
 *
 * ## It is an upper bound, deliberately
 *
 * `g - 1 + a` assumes you can land exactly on `g - 1`, which holds here because every
 * `add_quality` range starts at 1 and so can step by one. Where a pack ships only coarse steps
 * the true ceiling is lower and this over-estimates — which is the safe direction for a bound
 * that gates an input and a validator, since it never refuses an item the game can make.
 *
 * `undefined` means no ceiling could be derived: some currency adds quality with neither a gate
 * nor a use budget, so the answer is genuinely unbounded and callers must not invent one.
 */
export function maxQuality(snapshot) {
    /** The most quality one use of this currency can add. */
    const addedBy = (currency) => {
        const addOf = (ref) => {
            if (ref === null || typeof ref !== "object")
                return 0;
            const id = str(ref, "id");
            if (id === undefined)
                return 0;
            const mod = data(snapshot, CATEGORY.itemModification, id);
            if (mod === undefined || str(mod, "serializer") !== "add_quality")
                return 0;
            return minMax(obj(obj(mod, "data") ?? {}, "add_quality"), { min: 0, max: 0 }).max;
        };
        const always = arr(currency, "always_do_item_mods").reduce((sum, ref) => sum + addOf(ref), 0);
        const pick = arr(currency, "pick_one_item_mod").reduce((best, ref) => Math.max(best, addOf(ref)), 0);
        return always + pick;
    };
    let gatedCeiling = 0;
    let hasQualityCurrencies = false;
    /** `use_id` → the budget it allows and the best per-use add among the currencies sharing it. */
    const budgets = new Map();
    for (const id of ids(snapshot, CATEGORY.currency)) {
        const currency = data(snapshot, CATEGORY.currency, id);
        if (currency === undefined)
            continue;
        const add = addedBy(currency);
        if (add <= 0)
            continue;
        hasQualityCurrencies = true;
        // Every requirement must pass, so the tightest gate and the tightest budget are the ones
        // that bind.
        let gate;
        let budget;
        for (const ref of arr(currency, "req")) {
            if (typeof ref !== "string")
                continue;
            const req = data(snapshot, CATEGORY.itemRequirement, ref);
            if (req === undefined)
                continue;
            const reqData = obj(req, "data") ?? {};
            if (str(req, "serializer") === "is_under_quality") {
                // A gate whose number will not read is no gate: leaving it out drops this currency into
                // the unbounded branch below, which is the honest answer for data we cannot parse.
                // Defaulting it to infinity instead would silently make the ceiling infinite.
                const max = num(reqData, "max_quality", Number.NaN);
                if (Number.isFinite(max) && (gate === undefined || max < gate))
                    gate = max;
            }
            else if (str(req, "serializer") === "max_uses") {
                const uses = num(reqData, "max_uses", 0);
                const useId = str(reqData, "use_id");
                if (useId !== undefined && (budget === undefined || uses < budget.uses)) {
                    budget = { useId, uses };
                }
            }
        }
        if (gate !== undefined) {
            gatedCeiling = Math.max(gatedCeiling, gate - 1 + add);
        }
        else if (budget !== undefined) {
            const seen = budgets.get(budget.useId);
            budgets.set(budget.useId, {
                uses: seen === undefined ? budget.uses : Math.min(seen.uses, budget.uses),
                add: Math.max(seen?.add ?? 0, add),
            });
        }
        else {
            // Neither gated nor rationed: this currency can be applied to the same item for ever.
            return undefined;
        }
    }
    if (!hasQualityCurrencies)
        return undefined;
    let ceiling = gatedCeiling;
    for (const { uses, add } of budgets.values())
        ceiling += uses * add;
    return ceiling;
}
// ---------------------------------------------------------------------------
// Slot occupancy
// ---------------------------------------------------------------------------
/**
 * How many items may share one gear slot.
 *
 * Phase 0.5 left this open ("how many items share a gear slot (two rings?) is not in the
 * datapack... Left unchecked rather than invented") and the gear panel has carried a banner
 * saying so ever since. It *is* answerable — just not from the datapack.
 * `characters/CharacterEquipment.java` lays out a character's worn loadout explicitly, because
 * switching characters has to move every worn stack somewhere:
 *
 *     VANILLA_SLOTS = HEAD, CHEST, LEGS, FEET, OFFHAND      // indices 0-4
 *     CURIO_BLOCKS  = RING(base 5, count 2),
 *                     NECKLACE(base 7, count 1),
 *                     OMEN(base 8, count 1)
 *     public static final int SIZE = 9; // 4 armor + offhand + 2 rings + necklace + omen
 *
 * Two things that list settles beyond the counts:
 *
 *   - **The mainhand is not in it.** The file's header says the inventory and the mainhand are
 *     "deliberately left alone" — a weapon is the held item, so there is exactly one of it, and
 *     it does not travel with the character.
 *   - **Curios is not the wrong tree to be barking up.** The phase 0.5 note that Curios' "only
 *     registered slot here is `master_bag`" pointed the wrong way: `RefCurios` also registers
 *     `ring`, `omen` and `necklace`, and `GearSlot.isItemOfThisSlot` resolves both jewellery
 *     slots through `CuriosApi.getCuriosHelper().getCurioTags(item)`.
 *
 * Keyed by `mmorpg_gear_slot` id. A slot absent from this map has **no known capacity** and is
 * deliberately not guessed: `head` is a pack-added `Jewelry`-family slot matching no block in
 * `CURIO_BLOCKS`, so how many a character may wear is genuinely unanswered and
 * {@link slotCapacity} returns `undefined` rather than inventing a 1.
 *
 * **`elytra` is the one entry here that does not come from the Java.** It is a pack-added slot
 * like `head`, so `CURIO_BLOCKS` says nothing about it, and it was left out for that reason.
 * It is 1 on the pack author's own report: the pack gives a character exactly one elytra slot,
 * and the slot holds a unique that always grants the same stats. Recorded as a stated fact
 * rather than a derived one, because a future reader checking it against
 * `CharacterEquipment.java` will not find it there.
 */
export const SLOT_CAPACITY = {
    helmet: 1,
    chest: 1,
    pants: 1,
    boots: 1,
    necklace: 1,
    ring: 2,
    elytra: 1,
};
/**
 * The capacity of one `mmorpg_gear_slot`, or `undefined` when the mod does not say.
 *
 * Weapon and offhand slots are families — a character has one mainhand and one offhand, and
 * which *kind* fills it is the player's choice — so the limit belongs to the family rather than
 * to `sword` or `shield` individually. Ask {@link slotFamilyCapacity} for those.
 */
export function slotCapacity(slotId) {
    return SLOT_CAPACITY[slotId];
}
/**
 * Capacity per `SlotFamily`, for the two families where the family is the real limit.
 *
 * `Weapon` is the mainhand and `OffHand` is `EquipmentSlot.OFFHAND`: one each, whichever base
 * fills it. `Armor` and `Jewelry` are per-slot instead ({@link SLOT_CAPACITY}), because a
 * character wears four separate armour pieces and three separate jewels.
 */
export const FAMILY_CAPACITY = {
    Weapon: 1,
    OffHand: 1,
};
export function slotFamilyCapacity(family) {
    return FAMILY_CAPACITY[family];
}
export function socketFamily(family) {
    if (family === "Weapon")
        return "Weapon";
    if (family === "Jewelry")
        return "Jewelry";
    return "Armor";
}
/** The family a gear base's socketables resolve against. */
export function socketFamilyOfBase(snapshot, baseId) {
    const slot = baseGearType(snapshot, baseId)?.gearSlot;
    return socketFamily(slot === undefined ? undefined : slotFamily(snapshot, slot));
}
function modList(node, key) {
    return arr(node, key).filter((s) => s !== null && typeof s === "object" && !Array.isArray(s));
}
function familyStats(d) {
    return {
        onWeapons: modList(d, "on_weapons_stats"),
        onJewelry: modList(d, "on_jewelry_stats"),
        onArmor: modList(d, "on_armor_stats"),
    };
}
/** `getFor(fam)` over an already-read view. */
export function statsForFamily(view, family) {
    if (family === "Weapon")
        return view.onWeapons;
    if (family === "Jewelry")
        return view.onJewelry;
    return view.onArmor;
}
export function gem(snapshot, id) {
    const d = data(snapshot, CATEGORY.gem, id);
    if (!d)
        return undefined;
    return {
        id,
        gemType: str(d, "gem_type") ?? id,
        tier: num(d, "tier", 0),
        rarity: str(d, "rar") ?? "common",
        minLvlMulti: num(d, "min_lvl_multi", 0),
        ...familyStats(d),
    };
}
export function rune(snapshot, id) {
    const d = data(snapshot, CATEGORY.rune, id);
    if (!d)
        return undefined;
    return {
        id,
        tier: num(d, "tier", 0),
        minLvlMulti: num(d, "min_lvl_multi", 0),
        usesUnluckyRan: bool(d, "uses_unlucky_ran", false),
        ...familyStats(d),
    };
}
export function runeword(snapshot, id) {
    const d = data(snapshot, CATEGORY.runeword, id);
    if (!d)
        return undefined;
    return {
        id,
        runes: arr(d, "runes").filter((r) => typeof r === "string"),
        slots: arr(d, "slots").filter((s) => typeof s === "string"),
        stats: modList(d, "stats"),
    };
}
/**
 * `RuneWord.canApplyOnItem(GearItemData)` — one line, and it is the *slot*, not the family.
 *
 *     return slots.stream().anyMatch(x -> gear.GetBaseGearType().gear_slot.equals(x));
 *
 * So a runeword listing `chest` goes on a chest and nothing else, and a picker that offered it
 * for a helmet would be offering an item the game cannot make.
 */
export function runewordFitsBase(snapshot, view, baseId) {
    const slot = baseGearType(snapshot, baseId)?.gearSlot;
    return slot !== undefined && view.slots.includes(slot);
}
/**
 * Every runeword this item could carry: the rarity must allow runewords at all, and the base's
 * slot must be one the runeword names. Sorted by rune count, longest first, because that is the
 * order `RuneItem` itself resolves ties in — it takes the biggest match.
 */
export function runewordsForItem(snapshot, baseId, rarity) {
    if (rarity !== undefined && !rarity.canHaveRunewords)
        return [];
    return ids(snapshot, CATEGORY.runeword)
        .map((id) => runeword(snapshot, id))
        .filter((r) => r !== undefined && r.runes.length > 0)
        .filter((r) => runewordFitsBase(snapshot, r, baseId))
        .sort((a, b) => b.runes.length - a.runes.length || a.id.localeCompare(b.id));
}
/**
 * `RuneWord.hasMatchingRunesToCreate` — and it is a **substring** test, not a set test:
 *
 *     String reqString  = join(runes, "");
 *     String testString = join(socketed.map(x -> x.g), "");
 *     return testString.contains(reqString);
 *
 * The socketed ids are concatenated in socket order and the runeword's the same way, so the
 * runes must appear consecutively and in the declared order. `ano`+`net`+`mos` completes
 * Abyssal Depths; `net`+`ano`+`mos` does not.
 *
 * Concatenating ids is the mod's own trick and it is as fragile as it looks — this reproduces
 * it exactly rather than improving on it, because a planner that accepted an order the game
 * rejects would be describing an item nobody can build.
 */
export function runewordMatches(view, socketedRunes) {
    return socketedRunes.join("").includes(view.runes.join(""));
}
// ---------------------------------------------------------------------------
// Uniques
// ---------------------------------------------------------------------------
/**
 * Every unique in the snapshot, sorted by id.
 *
 * {@link uniquesForBase} answers "what can this base become"; this answers "what uniques
 * exist", which is what a picker wants when the player is choosing the unique *first* and
 * expects the base to follow from it.
 */
export function allUniques(snapshot) {
    return ids(snapshot, CATEGORY.unique)
        .map((id) => unique(snapshot, id))
        .filter((u) => u !== undefined)
        .sort((a, b) => a.id.localeCompare(b.id));
}
/**
 * The gear rarity a unique item takes.
 *
 * `UniqueGear.rarity` names it when present; otherwise the single rarity carrying
 * `is_unique_item` is the only thing it can be. That flag is what `AffixData.randomizeTier`
 * filters *out* of the affix tier pool, so there is exactly one in this pack (`unique`) and
 * falling back to it is a lookup rather than a guess.
 */
export function uniqueRarityId(snapshot, view) {
    if (view.rarity !== undefined && has(snapshot, CATEGORY.gearRarity, view.rarity)) {
        return view.rarity;
    }
    return ids(snapshot, CATEGORY.gearRarity).find((id) => gearRarity(snapshot, id)?.isUniqueItem);
}
// ---------------------------------------------------------------------------
// Core stats and the stat point budget
// ---------------------------------------------------------------------------
/**
 * The serializer id every allocatable core stat carries.
 *
 * `AllocateStatPacket.onReceived` rejects anything that is not a `CoreStat`:
 *
 *     // unregistered ids fall back to EmptyStat, so this also rejects a made up stat id
 *     if (!(ExileDB.Stats().get(stat) instanceof CoreStat)) { return; }
 *
 * and `CoreStat.SER_ID` is `"core_stat"`. In this pack that is exactly `strength`, `dexterity`
 * and `intelligence`. The 20 `bonus_stat_per_effect` stats carry a `core_stat_data` block too
 * and are *not* `CoreStat` instances, so matching on the serializer rather than on the presence
 * of that block is what keeps `aoe_per_power_charge` out of the allocation screen.
 */
export const CORE_STAT_SER = "core_stat";
/** The stats a level-up point may be spent on, derived rather than hardcoded. */
export function coreStatIds(snapshot) {
    return ids(snapshot, CATEGORY.stat)
        .filter((id) => entry(snapshot, CATEGORY.stat, id)?.data?.["ser"] === CORE_STAT_SER)
        .sort();
}
/**
 * The stat point budget at a level, from `mmorpg_game_balance.player_points.STATS`.
 *
 * Same shape and same arithmetic as {@link pointBudget}, which only covers the three trees:
 * `PlayerPointsType.getFreePoints` is one method serving all six point types.
 *
 *     int current = data.base_points + (int) (lvl * data.points_per_lvl);
 *     int total   = current + bonus;
 *     if (total > data.max_total_points) { total = data.max_total_points; }
 *     int free = total - getPointsInUse(p);
 *
 * For `STATS` in `original_balance` that is `0 + 1 * lvl`, capped at 300, with up to 50 bonus
 * points from sources a build document cannot see — hence `ceiling` above `fromLevel`, and a
 * warning rather than an error when a document spends into that band.
 */
/**
 * Every key of `player_points`, which is `PlayerPointsType` in the game.
 *
 * `pointBudget` covers the three that are trees; spell schools spend from `SPELLS` and
 * `PASSIVES`, which belong to no tree, so the general form is the one to reach for.
 */
export const PLAYER_POINT_TYPES = [
    "TALENTS",
    "ASCENDANCY",
    "ATLAS",
    "SPELLS",
    "PASSIVES",
    "STATS",
];
/**
 * The budget for any point pool, from `mmorpg_game_balance.player_points`.
 *
 *     int current = data.base_points + (int) (lvl * data.points_per_lvl);
 *     int total = Math.min(current + getBonusPoints(p), data.max_total_points);
 *
 * `getBonusPoints` is quest and item rewards, which no document can derive — so `fromLevel` is
 * what levelling alone grants and `ceiling` is what a character could conceivably hold.
 */
/**
 * Bonus points Craft to Exile 2 actually hands out, where the balance file's cap is not it.
 *
 * `max_bonus_points` is a *ceiling on* `getBonusPoints`, not a statement of how many the pack
 * awards, and for `ATLAS` the two are far apart: the balance file carries the mod's own
 * `PlayerPointsConfig(ATLAS, 0, 0, 200, 200)` (GeneratedData.java:132) while the points
 * themselves come one at a time from dungeon-realm atlas nodes —
 * `pd.points.get(ATLAS).giveBonusPoints(node.atlas_points_reward)` — and the pack's real total is
 * **104**. Left at 200 a planner offered twice the atlas tree anyone can allocate, and the
 * validator would not have called 150 points illegal.
 *
 * **Stated, not derived**, and deliberately: summing `atlas_points_reward` over the 151 entries
 * of `dungeon_realm_atlas_node` gives 117, or 90 once the uber-gated nine are removed, and
 * neither is 104 — which node is reachable depends on the atlas grid's own connectivity and on
 * progression this snapshot does not describe. So it is a number read off the game, carried here
 * the way {@link PACK_IN_COMBAT_REGEN_MULTI} carries the server config's, rather than a
 * derivation that happens to be wrong.
 *
 * `TALENTS` and `ASCENDANCY` need no entry: 101 from levelling + 25 bonus is the 126 the game
 * reports at level 100, and `ASCENDANCY`'s 0 + 9 is 9. Both are confirmed against
 * `character.pointTotals` on the 2026-09-17 capture.
 */
export const PACK_MAX_BONUS_POINTS = {
    ATLAS: 104,
};
/**
 * What finishing the campaign's epilogue is worth, per pool.
 *
 * `getBonusPoints` is quest and item rewards, and nothing derivable from a level reaches it — so
 * without this a level-100 planner offered 50 passive points and 100 spell points while the game
 * offers **54 and 110**. Four passives and ten spell points is not a rounding error on a 50-point
 * tree; it is four nodes you cannot plan for.
 *
 * Stated rather than derived, for the reason {@link PACK_MAX_BONUS_POINTS} is: the reward lives
 * in the quest chain, which is not in any datapack this snapshot reads. Both numbers are read off
 * the finished character — 0.5/level gives 50 passives at 100 and the game reports 54; 1/level
 * gives 100 spell points and the game reports 110 — and both sit inside the balance file's own
 * `max_bonus_points` of 10, which is the ceiling `pointsAvailable` still clamps to.
 *
 * `TALENTS`, `STATS` and `ASCENDANCY` are absent because their bonuses are not this quest:
 * `character.pointTotals` on a finished character reports the talent tree's 126 as 101 from
 * levelling plus its own 25, which the tree screen already reads off the capture.
 */
export const EPILOGUE_BONUS_POINTS = {
    PASSIVES: 4,
    SPELLS: 10,
};
export function playerPointBudget(snapshot, type, level, balanceId = DEFAULT_BALANCE_ID) {
    const d = data(snapshot, CATEGORY.gameBalance, balanceId);
    if (!d)
        return undefined;
    const points = obj(d, "player_points");
    const node = points ? obj(points, type) : undefined;
    if (!node)
        return undefined;
    const maxTotal = num(node, "max_total_points", 0);
    // The pack's own number wins where there is one, and never raises the balance file's cap:
    // an override is a statement about what this pack awards, not a licence to exceed the mod.
    const declaredBonus = num(node, "max_bonus_points", 0);
    const override = PACK_MAX_BONUS_POINTS[type];
    const maxBonus = override === undefined ? declaredBonus : Math.min(override, declaredBonus);
    const fromLevel = Math.min(Math.floor(num(node, "base_points", 0) + num(node, "points_per_lvl", 0) * level), maxTotal);
    return { fromLevel, maxBonus, maxTotal, ceiling: Math.min(fromLevel + maxBonus, maxTotal) };
}
export function pointsAvailable(snapshot, character, type, balanceId = DEFAULT_BALANCE_ID) {
    const budget = playerPointBudget(snapshot, type, character.level, balanceId);
    const recorded = character.pointTotals?.[type];
    if (typeof recorded === "number" && Number.isFinite(recorded) && recorded >= 0) {
        return { total: Math.floor(recorded), recorded: true, budget };
    }
    // The quest reward is a `getBonusPoints` grant like any other, so it is bounded by the same two
    // numbers the game bounds one by — `max_bonus_points` and then `max_total_points`:
    //
    //     int total = Math.min(current + getBonusPoints(p), data.max_total_points);
    //
    // Not a third rule beside the budget, which is why it goes through `maxBonus` rather than being
    // added to `fromLevel` and returned.
    const fromLevel = budget?.fromLevel ?? 0;
    if (character.questsComplete !== true)
        return { total: fromLevel, recorded: false, budget };
    const bonus = Math.min(EPILOGUE_BONUS_POINTS[type] ?? 0, budget?.maxBonus ?? 0);
    const total = budget === undefined ? fromLevel : Math.min(fromLevel + bonus, budget.maxTotal);
    return { total, recorded: false, budget };
}
/** `points_per_lvl` for a pool — the SPELLS one drives the per-level cost of a school perk. */
export function pointsPerLevel(snapshot, type, balanceId = DEFAULT_BALANCE_ID) {
    const d = data(snapshot, CATEGORY.gameBalance, balanceId);
    const points = d ? obj(d, "player_points") : undefined;
    const node = points ? obj(points, type) : undefined;
    return node ? num(node, "points_per_lvl", 0) : 0;
}
export function statPointBudget(snapshot, level, balanceId = DEFAULT_BALANCE_ID) {
    return playerPointBudget(snapshot, "STATS", level, balanceId);
}
// ---------------------------------------------------------------------------
// Omens
// ---------------------------------------------------------------------------
/**
 * The three `GearRarityType` values omen requirements are counted over.
 *
 * Not the rarities themselves: `common` through `mythic` are all `NORMAL`, `runeword` is
 * `RUNED` and `unique` is `UNIQUE`. So a requirement for two `NORMAL` pieces is met by a
 * common and a mythic together.
 */
export const GEAR_RARITY_TYPES = ["NORMAL", "UNIQUE", "RUNED"];
export function isGearRarityType(value) {
    return GEAR_RARITY_TYPES.includes(value);
}
export function omen(snapshot, id) {
    const d = data(snapshot, CATEGORY.omen, id);
    if (!d)
        return undefined;
    return {
        id,
        levelRequirementFraction: num(d, "lvl_req", 0),
        mods: arr(d, "mods").filter((m) => m !== null && typeof m === "object" && !Array.isArray(m)),
        affixTypes: arr(d, "affix_types").filter((t) => typeof t === "string"),
    };
}
export function omenIds(snapshot) {
    return ids(snapshot, CATEGORY.omen).sort();
}
/** The level an omen starts dropping at: `MAX_LEVEL * lvl_req`, per `OmenPart`. */
export function omenMinLevel(snapshot, view, balanceId = DEFAULT_BALANCE_ID) {
    return Math.ceil(maxLevel(snapshot, balanceId) * view.levelRequirementFraction);
}
/**
 * The roll percent an omen's own mods resolve at — earned from its requirements, never rolled.
 *
 *     public static int getStatPercent(HashMap<GearRarityType, Integer> rarities,
 *                                      List<OmenSlotReq> slot_req, GearRarity rar) {
 *         int num = 0;
 *         for (Map.Entry<GearRarityType, Integer> en : rarities.entrySet()) {
 *             num += en.getValue() * 10;
 *         }
 *         num += slot_req.size() * 10;
 *         num *= rar.omens.stat_multi;
 *         return num;
 *     }
 *
 * — `OmenData.java`, whose own comment is "the more difficult the omen is to assemble, the
 * more stats it provides".
 *
 * Two details that are behaviour rather than tidiness, and are reproduced rather than fixed:
 *
 *  - **The multiply truncates.** `num` is an `int` and `stat_multi` a `float`, so Java's
 *    compound assignment narrows: `70 * 1.25` is 87, not 87.5.
 *  - **Nothing clamps it to 100.** `ExactStatData.fromStatModifier` is a bare
 *    `min + (max - min) * percent / 100F`, so a heavily-conditioned mythic omen reaching 125
 *    puts its stats a quarter above the declared maximum. That is what the game does.
 */
export function omenStatPercent(snapshot, requires, slotRequirementCount, rarityId) {
    let num = 0;
    for (const count of Object.values(requires ?? {})) {
        if (Number.isFinite(count))
            num += count * 10;
    }
    num += slotRequirementCount * 10;
    const multi = omenStatMulti(snapshot, rarityId);
    // Java narrows the float back into the int on `num *= multi`, truncating toward zero.
    return Math.trunc(num * multi);
}
/** `GearRarity.omens.stat_multi` — the difficulty payout scalar for a rarity. */
export function omenStatMulti(snapshot, rarityId) {
    const d = data(snapshot, CATEGORY.gearRarity, rarityId);
    const omens = d ? obj(d, "omens") : undefined;
    return omens ? num(omens, "stat_multi", 1) : 1;
}
/**
 * How many worn pieces count toward the omen — `PlayerData.omensFilled`.
 *
 * Ported from `OmenData.calcPiecesEquipped`, which is subtler than it looks:
 *
 *     for (GearData gear : Load.Unit(p).equipmentCache.getGear()) {
 *         if (gear.gear != null) {
 *             var type = gear.gear.getRarity().type;
 *             boolean has = true;
 *             for (OmenSlotReq slot : slot_req) {
 *                 String gearslot = gear.gear.GetBaseGearType().gear_slot;
 *                 if (slot.slot.equals(gearslot)) { if (type != slot.rtype) { has = false; } }
 *             }
 *             if (has) {
 *                 if (map.getOrDefault(type, 0) < rarities.getOrDefault(type, 0)) {
 *                     map.put(type, map.getOrDefault(type, 0) + 1);
 *                 }
 *             }
 *         }
 *     }
 *
 * Three consequences worth naming:
 *
 *  - **A slot requirement disqualifies, it does not add.** A piece in a named slot whose type
 *    does not match stops counting entirely; a piece in a slot nobody named is unaffected.
 *  - **Each type is capped at what the omen asked for.** Six `NORMAL` pieces against a
 *    requirement of two contribute two. So the count cannot exceed the threshold, which is
 *    what makes "all buckets at or below `fill`" a sensible payout rule.
 *  - **A type the omen did not ask for contributes nothing**, because the cap it is compared
 *    against is `getOrDefault(type, 0)` — zero.
 *
 * And the piece list **includes the mainhand**. `recalcGears` collects `CHEST, FEET, LEGS,
 * HEAD, OFFHAND` plus every curio slot, and the weapon is tracked separately in `recalcWeapon`
 * — but `CachedEntityStats.getGear()`, which is what this loop iterates, copies that list and
 * appends the weapon (read off the **6.4.13 jar**; the fork's
 * `// todo note somewhere the weapon isnt included in omen counting` describes an older
 * `getGear`). Both halves filter on `isUsableBy`, so a piece above the character's level is
 * not worn as far as this is concerned.
 */
export function countOmenPieces(snapshot, items, omenSetup, characterLevel) {
    const required = omenSetup.requires ?? {};
    const slotReqs = omenSetup.slotRequirements ?? [];
    const counted = new Map();
    for (const item of items) {
        const base = baseGearType(snapshot, item.base);
        const slotId = base?.gearSlot;
        if (slotId === undefined)
            continue;
        // `isUsableBy` refuses an item above the holder's level, and `getGear()` filters on it.
        if (item.itemLevel > characterLevel)
            continue;
        const type = gearRarity(snapshot, item.rarity)?.type;
        if (type === undefined)
            continue;
        const disqualified = slotReqs.some((req) => req.slot === slotId && req.rarityType !== type);
        if (disqualified)
            continue;
        const already = counted.get(type) ?? 0;
        if (already < (required[type] ?? 0))
            counted.set(type, already + 1);
    }
    let total = 0;
    for (const count of counted.values())
        total += count;
    return total;
}
/**
 * Whether a gear slot can carry an omen slot requirement.
 *
 * `Omen.getRandomSlotReq` draws from `GearSlots().getFilterWrapped(x -> !x.fam.isWeapon())`,
 * so the generator never names a weapon slot. That is a rule about requirements only: the
 * mainhand still *counts* toward the pieces (see {@link countOmenPieces}).
 */
export function omenSlotRequirementAllowed(snapshot, slotId) {
    const family = slotFamily(snapshot, slotId);
    return family !== undefined && family !== "Weapon";
}
export function omenBuckets(snapshot, setup) {
    const view = omen(snapshot, setup.id);
    if (view === undefined)
        return [];
    let max = 0;
    for (const count of Object.values(setup.requires ?? {})) {
        if (Number.isFinite(count))
            max += count;
    }
    const percent = omenStatPercent(snapshot, setup.requires, (setup.slotRequirements ?? []).length, setup.rarity);
    const out = [{ pieces: max, mods: view.mods, statPercent: percent }];
    let index = max - 1;
    for (const affix of setup.affixes ?? []) {
        // `adata.rar = rar.GUID(); adata.p = getStatPercent(...)` — both come from the omen.
        out.push({ pieces: index, statPercent: percent, affix: omenAffixRoll(affix, setup, percent) });
        index--;
        if (index < 2)
            index = 2;
    }
    return out;
}
/**
 * One of an omen's affixes as the game stores it: the document's affix id, the omen's own
 * rarity as the tier, and the derived percent as the roll.
 *
 * Exported because the editor writes the same shape back into the document — a stored value
 * that drifts from this one is a document the game could not have produced, and the cheapest
 * place to stop that is where it is written.
 */
export function omenAffixRoll(roll, setup, percent) {
    return { affixId: roll.affixId, tier: setup.rarity, rollPercent: percent };
}
//# sourceMappingURL=queries.js.map